import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { Eye, Code } from 'lucide-react';
import { SanitizedContent } from '@/components/ui/sanitized-content';

interface HTMLContentEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const HTMLContentEditor: React.FC<HTMLContentEditorProps> = ({
  value,
  onChange,
  placeholder = "Enter your content...",
  className
}) => {
  const [activeView, setActiveView] = useState<'edit' | 'preview'>('edit');

  return (
    <div className={cn("border rounded-md", className)}>
      <Tabs value={activeView} onValueChange={(v) => setActiveView(v as 'edit' | 'preview')}>
        <div className="border-b bg-muted/50 px-3 py-2">
          <TabsList className="h-8">
            <TabsTrigger value="edit" className="text-xs">
              <Code className="w-3 h-3 mr-1" />
              Edit
            </TabsTrigger>
            <TabsTrigger value="preview" className="text-xs">
              <Eye className="w-3 h-3 mr-1" />
              Preview
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="edit" className="m-0 p-0">
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="min-h-[500px] rounded-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 font-mono text-sm resize-none"
          />
        </TabsContent>

        <TabsContent value="preview" className="m-0 p-0">
          <SanitizedContent
            content={value || '<p class="text-muted-foreground">No content to preview</p>'}
            className="min-h-[500px] p-4 prose prose-sm max-w-none overflow-auto"
          />
        </TabsContent>
      </Tabs>

      <div className="border-t bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        {value.length > 0 && (
          <>
            {Math.round(value.length / 1024)}KB • {value.split(/\s+/).filter(w => w.length > 0).length} words
          </>
        )}
      </div>
    </div>
  );
};
